import { useMutation, useQuery } from '@apollo/client'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'

import {
  IPrepItemBatchStandardsType,
  IPrepItemSettingsType,
  getPrepItemBatchStandards,
  getPrepItemSettings,
  updatePrepItemDisplayOnHandBatchTypeId,
} from '../gql'

interface IEditOnHandUnitTypeModalProps {
  visible: boolean
  onClose: () => void
  prepItemId: string
  prepItemName: string
  onSuccess?: () => void
}

interface IBatchTypeOption {
  prepBatchType: string
  batchTypeId: number
  sourceType: 'PREP' | 'ONHAND'
}

const EditOnHandUnitTypeModal = ({
  visible,
  onClose,
  prepItemId,
  prepItemName,
  onSuccess,
}: IEditOnHandUnitTypeModalProps) => {
  const [useStandardUnit, setUseStandardUnit] = useState(false)
  const [selectedBatchTypeId, setSelectedBatchTypeId] = useState<number | null>(
    null,
  )
  const [prepOptions, setPrepOptions] = useState<IBatchTypeOption[]>([])
  const [onHandOptions, setOnHandOptions] = useState<IBatchTypeOption[]>([])
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const { data } = useQuery<IPrepItemBatchStandardsType>(
    getPrepItemBatchStandards,
  )

  const { data: settingsData } =
    useQuery<IPrepItemSettingsType>(getPrepItemSettings)

  const [updateOnHandBatchTypeMutation] = useMutation(
    updatePrepItemDisplayOnHandBatchTypeId,
  )

  useEffect(() => {
    if (
      prepItemId &&
      visible &&
      data?.getPrepItemBatchStandards &&
      settingsData?.getPrepItemSettings
    ) {
      // Filter batch standards for this prep item
      const itemBatchStandards = data.getPrepItemBatchStandards.nodes.filter(
        (item) => item.prepItemIdCode === prepItemId,
      )

      // Separate PREP and ONHAND options
      const prepBatchTypes = itemBatchStandards
        .filter((item) => item.sourceType === 'PREP')
        .map((item) => ({
          prepBatchType: item.prepBatchType,
          batchTypeId: item.batchTypeId,
          sourceType: item.sourceType,
        }))

      const onHandBatchTypes = itemBatchStandards
        .filter((item) => item.sourceType === 'ONHAND')
        .map((item) => ({
          prepBatchType: item.prepBatchType,
          batchTypeId: item.batchTypeId,
          sourceType: item.sourceType,
        }))

      setPrepOptions(prepBatchTypes)
      setOnHandOptions(onHandBatchTypes)

      // Get the current displayOnHandBatchTypeId from settings
      const itemSettings = settingsData.getPrepItemSettings.nodes.find(
        (setting) => setting.prepItemIdCode === prepItemId,
      )

      if (itemSettings?.displayOnHandBatchTypeId) {
        // Find the corresponding batch type
        const selectedItem = itemBatchStandards.find(
          (item) => item.batchTypeId === itemSettings.displayOnHandBatchTypeId,
        )

        if (selectedItem) {
          setUseStandardUnit(false)
          setSelectedBatchTypeId(selectedItem.batchTypeId)
        } else {
          setUseStandardUnit(true)
          setSelectedBatchTypeId(null)
        }
      } else {
        setUseStandardUnit(true)
        setSelectedBatchTypeId(null)
      }

      setError(null)
      setSuccess(false)
    }
  }, [prepItemId, visible, data, settingsData])

  const handleStandardUnitChange = (checked: boolean) => {
    setUseStandardUnit(checked)
    if (checked) {
      setSelectedBatchTypeId(null)
    }
  }

  const handleBatchTypeChange = (batchTypeId: number, checked: boolean) => {
    if (checked) {
      setSelectedBatchTypeId(batchTypeId)
      setUseStandardUnit(false)
    } else {
      setSelectedBatchTypeId(null)
    }
  }

  const handleSave = async () => {
    if (!prepItemId) return

    setIsLoading(true)
    setError(null)

    try {
      // Determine the displayOnHandBatchTypeId value
      // If useStandardUnit is true, set to null; otherwise use selectedBatchTypeId
      const displayOnHandBatchTypeId = useStandardUnit
        ? null
        : selectedBatchTypeId

      // Call the mutation
      await updateOnHandBatchTypeMutation({
        variables: {
          prepItemId: prepItemId,
          displayOnHandBatchTypeId: displayOnHandBatchTypeId,
        },
      })

      setSuccess(true)

      if (onSuccess) {
        onSuccess()
      }

      setTimeout(() => {
        onClose()
        setSuccess(false)
        setError(null)
        setIsLoading(false)
      }, 1500)
    } catch (err) {
      setError('Failed to update. Please try again.')
      setIsLoading(false)
    }
  }

  if (!prepItemId) return null

  return (
    <Dialog open={visible} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit On Hand Unit Type - {prepItemName}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={useStandardUnit}
                onChange={(e) => handleStandardUnitChange(e.target.checked)}
                size="small"
                disabled={isLoading}
              />
            }
            label="Standard Unit"
            sx={{ mb: 3 }}
          />

          {prepOptions.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, fontWeight: 600, color: '#666' }}
              >
                PREP
              </Typography>
              <Box sx={{ pl: 2 }}>
                {prepOptions.map((option) => (
                  <FormControlLabel
                    key={option.batchTypeId}
                    control={
                      <Checkbox
                        checked={selectedBatchTypeId === option.batchTypeId}
                        onChange={(e) =>
                          handleBatchTypeChange(
                            option.batchTypeId,
                            e.target.checked,
                          )
                        }
                        size="small"
                        disabled={isLoading}
                      />
                    }
                    label={option.prepBatchType}
                  />
                ))}
              </Box>
            </Box>
          )}

          {onHandOptions.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, fontWeight: 600, color: '#666' }}
              >
                ONHAND
              </Typography>
              <Box sx={{ pl: 2 }}>
                {onHandOptions.map((option) => (
                  <FormControlLabel
                    key={option.batchTypeId}
                    control={
                      <Checkbox
                        checked={selectedBatchTypeId === option.batchTypeId}
                        onChange={(e) =>
                          handleBatchTypeChange(
                            option.batchTypeId,
                            e.target.checked,
                          )
                        }
                        size="small"
                        disabled={isLoading}
                      />
                    }
                    label={option.prepBatchType}
                  />
                ))}
              </Box>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Successfully updated
            </Alert>
          )}

          <Box
            sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 4 }}
          >
            <Button onClick={onClose} variant="outlined" disabled={isLoading}>
              Close
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={success || isLoading}
            >
              Save
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default EditOnHandUnitTypeModal
